/**
 * <PrintBarcodeLabels>
 *
 * Warehouse-friendly A4 barcode sticker sheet — 3 columns × 10 rows =
 * 30 labels per page (Avery L7160-compatible geometry: 63.5×25 mm each,
 * 5mm horizontal gutter, 15mm top margin). Uses jsbarcode to render
 * CODE128 SVGs for every product barcode, then `window.print()` to hand
 * the whole page to the browser's print dialog.
 *
 * UX flow:
 *  1. Page loads all products that have a `barcode` set.
 *  2. Operator ticks the products they want and dials in copies per
 *     product (default 1 label each).
 *  3. Preview shows a live A4 sheet with the exact grid the printer
 *     will output.
 *  4. Tap "Print" → browser print dialog opens with the labels
 *     already laid out on A4.
 *
 * Print CSS lives on the page itself (`@media print`) so no styles
 * bleed into other routes.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import JsBarcode from 'jsbarcode';
import { productsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
    ArrowLeft, Printer, Search, Loader2, Tag, Boxes, Check,
} from 'lucide-react';

const LABELS_PER_PAGE = 30; // 3 × 10

/**
 * Small memoised component that draws one CODE128 SVG barcode with
 * jsbarcode. Kept inline so we don't need a separate file for a 15-
 * line helper — the whole label-printing surface is self-contained.
 */
const BarcodeSvg = ({ value, height = 34 }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (!ref.current || !value) return;
        try {
            JsBarcode(ref.current, String(value), {
                format: 'CODE128',
                displayValue: true,
                fontSize: 10,
                height,
                margin: 0,
                textMargin: 1,
                width: 1.4,
            });
        } catch (e) {
            // jsbarcode throws for invalid values (e.g. empty string).
            // Rendering nothing is fine — the label just shows the SKU.
            console.warn('Barcode render failed for', value, e?.message);
        }
    }, [value, height]);
    return <svg ref={ref} />;
};

const PrintBarcodeLabels = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    // Map product.id → copies (integer, 0 or missing = not selected).
    const [copies, setCopies] = useState({});

    useEffect(() => {
        productsAPI.getAll()
            .then((r) => {
                const withBarcodes = (r.data || []).filter((p) => p.barcode);
                setProducts(withBarcodes);
            })
            .catch(() => toast.error('Failed to load products'))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return products;
        return products.filter((p) =>
            (p.name || '').toLowerCase().includes(q) ||
            (p.sku || '').toLowerCase().includes(q) ||
            (p.barcode || '').includes(q),
        );
    }, [products, query]);

    const setCopyCount = (id, val) => {
        const n = Math.max(0, parseInt(val, 10) || 0);
        setCopies((prev) => ({ ...prev, [id]: n }));
    };

    // Flatten the {productId: copies} map into a concrete list of
    // labels we're going to print. Order = table order (which is the
    // order the operator sees on-screen).
    const labels = useMemo(() => {
        const out = [];
        for (const p of products) {
            const n = copies[p.id] || 0;
            for (let i = 0; i < n; i++) out.push(p);
        }
        return out;
    }, [products, copies]);

    // Chunk labels into pages of 30 so page breaks land between rows,
    // never mid-label.
    const pages = useMemo(() => {
        const chunks = [];
        for (let i = 0; i < labels.length; i += LABELS_PER_PAGE) {
            chunks.push(labels.slice(i, i + LABELS_PER_PAGE));
        }
        return chunks;
    }, [labels]);

    const totalLabels = labels.length;
    const selectedProducts = Object.values(copies).filter((n) => n > 0).length;

    const selectAll = (n = 1) => {
        const next = {};
        for (const p of filtered) next[p.id] = n;
        setCopies((prev) => ({ ...prev, ...next }));
    };

    const clearAll = () => setCopies({});

    const doPrint = () => {
        if (totalLabels === 0) {
            toast.error('Pick at least one product and set copies ≥ 1');
            return;
        }
        // Delay one tick so any pending state renders before the print
        // dialog captures the DOM.
        setTimeout(() => window.print(), 50);
    };

    return (
        <div data-testid="print-labels-page" className="space-y-6 animate-fadeIn pb-24">
            {/* Screen-only header + controls */}
            <div className="print:hidden space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <Button
                            variant="ghost"
                            onClick={() => navigate('/products')}
                            data-testid="back-to-products"
                            className="-ml-3 mb-1 text-gray-500 hover:text-[#1A1A1A]"
                        >
                            <ArrowLeft size={16} className="mr-1" /> Back to Products
                        </Button>
                        <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">
                            Print Barcode Labels
                        </h1>
                        <p className="text-gray-500 mt-1">
                            30 labels per A4 sheet (3×10) — Avery L7160-compatible
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="px-4 py-2 bg-[#1A1A1A] text-white rounded-lg">
                            <p className="text-[10px] uppercase tracking-wider text-gray-400">
                                Total labels
                            </p>
                            <p className="font-mono font-bold text-lg" data-testid="total-labels-count">
                                {totalLabels}
                            </p>
                        </div>
                        <Button
                            onClick={doPrint}
                            disabled={totalLabels === 0}
                            data-testid="print-labels-btn"
                            className="bg-[#1A1A1A] hover:bg-[#C08478] text-white gap-2"
                        >
                            <Printer size={16} /> Print {totalLabels || ''}
                        </Button>
                    </div>
                </div>

                <Card className="border-0 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Tag size={16} className="text-[#C08478]" /> Pick products
                        </CardTitle>
                        <div className="flex items-center gap-2 flex-wrap">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => selectAll(1)}
                                data-testid="select-all-1"
                            >
                                <Check size={12} className="mr-1" /> Select filtered (×1)
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={clearAll}
                                data-testid="clear-all"
                            >
                                Clear all
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="mb-3 relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <Input
                                placeholder="Search by SKU, name, or barcode…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="pl-9 h-10 bg-[#F5F5F5]"
                                data-testid="search-input"
                            />
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center h-32 text-gray-400">
                                <Loader2 className="animate-spin mr-2" /> Loading…
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                                <Boxes size={32} className="mb-2 opacity-50" />
                                <p className="text-sm">
                                    {products.length === 0
                                        ? 'No products have a barcode yet — assign one first.'
                                        : 'No products match your search.'}
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-[#F5F5F5]">
                                            <TableHead className="w-24">Copies</TableHead>
                                            <TableHead>SKU</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Barcode</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filtered.map((p) => (
                                            <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                                                <TableCell>
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        step={1}
                                                        inputMode="numeric"
                                                        value={copies[p.id] || ''}
                                                        onChange={(e) => setCopyCount(p.id, e.target.value)}
                                                        placeholder="0"
                                                        className="h-9 w-20 text-center font-mono"
                                                        data-testid={`copies-input-${p.id}`}
                                                    />
                                                </TableCell>
                                                <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                                                <TableCell className="text-sm">{p.name}</TableCell>
                                                <TableCell className="font-mono text-xs text-gray-500">
                                                    {p.barcode}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Live on-screen preview of the first page so the
                    operator can spot obvious problems (missing barcode,
                    truncated name) BEFORE hitting print. */}
                {pages[0] && (
                    <Card className="border-0 shadow-sm">
                        <CardHeader>
                            <CardTitle className="text-base flex items-center justify-between">
                                <span>Preview — page 1 of {pages.length}</span>
                                <span className="text-xs text-gray-500 font-normal">
                                    {selectedProducts} product(s), {totalLabels} label(s)
                                </span>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="preview-sheet mx-auto">
                                <LabelGrid labels={pages[0]} />
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Print-only layer — every page rendered stacked, one
                after the other. `@media print` hides everything else
                so only these sheets reach the printer. */}
            <div className="print-only" data-testid="print-sheet-container">
                {pages.map((pageLabels, idx) => (
                    <div className="print-page" key={`page-${idx}`}>
                        <LabelGrid labels={pageLabels} />
                    </div>
                ))}
            </div>

            <style>{`
                /* Preview mimics the print sheet at a comfortable
                   on-screen size so the operator sees a faithful
                   representation. */
                .preview-sheet {
                    width: 210mm;
                    max-width: 100%;
                    background: white;
                    border: 1px solid #E5E5E5;
                    padding: 15mm 5mm;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    transform-origin: top left;
                }
                @media (max-width: 900px) {
                    .preview-sheet {
                        transform: scale(0.5);
                        margin-bottom: -50%;
                    }
                }

                .label-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 63.5mm);
                    grid-auto-rows: 25mm;
                    column-gap: 5mm;
                    row-gap: 0;
                    justify-content: center;
                }
                .label-cell {
                    box-sizing: border-box;
                    padding: 2mm 3mm;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    text-align: center;
                    font-family: 'Century Gothic', system-ui, sans-serif;
                }
                .label-cell .sku {
                    font-family: monospace;
                    font-size: 9px;
                    font-weight: 600;
                    line-height: 1.1;
                    color: #1A1A1A;
                }
                .label-cell .name {
                    font-size: 8px;
                    line-height: 1.15;
                    color: #333;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .label-cell svg {
                    width: 100% !important;
                    height: auto !important;
                    max-height: 12mm;
                }

                .print-only { display: none; }

                @media print {
                    @page {
                        size: A4 portrait;
                        margin: 15mm 5mm;
                    }
                    body * { visibility: hidden !important; }
                    .print-only, .print-only * { visibility: visible !important; }
                    .print-only {
                        display: block !important;
                        position: absolute;
                        top: 0; left: 0; right: 0;
                    }
                    .print-page {
                        page-break-after: always;
                        break-after: page;
                    }
                    .print-page:last-child {
                        page-break-after: auto;
                        break-after: auto;
                    }
                    .label-grid {
                        column-gap: 5mm;
                    }
                }
            `}</style>
        </div>
    );
};

/**
 * Renders one page's worth of labels (up to 30) as a CSS-grid. Pads
 * the trailing cells with empty placeholders so the grid stays 3-wide
 * even on the last, partially-filled page — otherwise the last row
 * would left-align and look ugly.
 */
const LabelGrid = ({ labels }) => {
    const padded = [...labels];
    while (padded.length < LABELS_PER_PAGE) padded.push(null);
    return (
        <div className="label-grid">
            {padded.map((p, i) => (
                <div className="label-cell" key={i}>
                    {p ? (
                        <>
                            <div className="sku">{p.sku}</div>
                            <BarcodeSvg value={p.barcode} />
                            <div className="name">{p.name}</div>
                        </>
                    ) : null}
                </div>
            ))}
        </div>
    );
};

export default PrintBarcodeLabels;
